import { useRef, useState } from 'react';
import api from 'api';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import {
  Avatar,
  Button,
  ConfirmButton,
  MoreOptions,
  RichTextValue,
  TextWithLineBreaks
} from 'components/common';
import { BoxRow } from 'components/common/box';
import { Form, TextArea } from 'components/common/form';
import { Icon } from 'components/common/icons';
import { AchievementItemAddedBy } from '.';

import type { FormApi } from 'informed';
import type { AchievementProgress, LocaleMessages } from 'shared-types';

const AnswerForm = ({
  initialValue,
  messages,
  onCancel,
  onConfirm
}: {
  initialValue?: string;
  messages: LocaleMessages;
  onCancel?: () => void;
  onConfirm: (text: string) => Promise<void>;
}) => {
  const formApi = useRef<FormApi>(null);

  const handleSubmit = async () => {
    const values = formApi.current.getState().values;
    await onConfirm(values.text as string);

    formApi.current.reset();
    return true;
  };

  return (
    <Form
      initialValues={{ text: initialValue || '' }}
      getApi={(form) => (formApi.current = form)}
    >
      {({ formState }) => (
        <>
          <TextArea
            autoGrow={true}
            error={formState?.errors?.text}
            field="text"
            id={`achievement-item-text`}
            infoMessage={messages.achievementUsersTemplateAnswerInfoGroups}
            placeholder={messages.commonAddAnswer}
            rows={3}
          />

          <ConfirmButton
            actionText={messages.commonSaveAnswer}
            buttonSize="small"
            buttonStyle={{ marginTop: 5 }}
            buttonText={messages.commonSaveAnswer}
            category="success"
            confirmText={messages.commonSaveAnswerConfirm}
            confirmTitle={messages.commonSaveAnswer}
            disabled={!(formState.values.text as string)?.trim()}
            onConfirm={handleSubmit}
            successMessage={messages.commonSaveAnswerSuccess}
          />

          {onCancel ? (
            <Button
              category="light"
              onClick={onCancel}
              size="small"
              text={messages.commonCancel}
              style={{ marginLeft: 10 }}
            />
          ) : null}
        </>
      )}
    </Form>
  );
};

interface Props {
  achievement?: {
    id: number;
    isOwnerOrSuperadmin?: boolean;
    isSupervisor?: boolean;
    userAdminPermissions?: string[];
  };
  canAnswer: boolean;
  canMarkComplete: boolean;
  completionId: number;
  item: AchievementProgress['template'][number];
  itemIndex: number;
  updateItems: () => Promise<void>;
}

const AchievementGroupsOfUsersGroupFillTemplate = ({
  achievement,
  canAnswer,
  canMarkComplete,
  completionId,
  item,
  itemIndex,
  updateItems
}: Props) => {
  const { messages, theme, user } = useReduxState();

  const [editingAnswerId, setEditingAnswerId] = useState<number>(null);

  const handleAddAnswer = async (text: string) => {
    await api.post(
      `/admin/achievements/${achievement.id}/group-completions/${completionId}/items`,
      { itemId: item.id, text }
    );
    await updateItems();
  };

  const handleDeleteAnswer = async (answerId: number) => {
    await api.delete(
      `/admin/achievements/${achievement.id}/group-completions/${completionId}/items/${answerId}`
    );
    await updateItems();
    return true;
  };

  const handleUpdateAnswer = async (answerId: number, text: string) => {
    await api.put(
      `/admin/achievements/${achievement.id}/group-completions/${completionId}/items/${answerId}`,
      { itemId: item.id, text }
    );
    await updateItems();

    setEditingAnswerId(null);
  };

  const handleToggleItemCompletion = async () => {
    await api.post(
      `/admin/achievements/${achievement.id}/group-completions/${completionId}/mark-item/${item.id}`
    );
    await updateItems();
    return true;
  };

  const canCreateAnswer =
    Boolean(completionId) && canAnswer && !item.isCompleted;

  return (
    <BoxRow key={item.id}>
      <div className="content">
        <div className="status">
          <Avatar
            size="24px"
            fontSize="1.4rem"
            padding="2px 0"
            {...(item.isCompleted
              ? {
                  backgroundColor: theme.successLightColor,
                  color: theme.successLightTextColor
                }
              : {})}
          >
            {itemIndex}
          </Avatar>

          {item.isCompleted ? (
            <Icon
              icon="check"
              style={{ color: theme.successColor }}
              {...(item.isCompletedOn
                ? {
                    'data-tooltip': getMessageWithPlaceholders({
                      message:
                        messages.achievementGroupCompletionMarkCompletedTooltip,
                      placeholders: {
                        user: item.isCompletedMarkedByUser
                          ? item.isCompletedMarkedByUser.email ||
                            item.isCompletedMarkedByUser.username
                          : messages.commonAnonymous,
                        date: getFormattedDate({ date: item.isCompletedOn })
                      }
                    })
                  }
                : {})}
            />
          ) : null}
        </div>

        <div className="item">
          <div className="item-inner">
            <div className="item-text">
              <RichTextValue html={item.question} />
            </div>

            <div className="answers">
              {item.answers.map((answer) => {
                const canEditAnswer =
                  canCreateAnswer && answer.user?.id === user?.id;

                return (
                  <div key={answer.id}>
                    <div className="answer">
                      <div className="comment">
                        {editingAnswerId === answer.id ? (
                          <AnswerForm
                            initialValue={answer.text}
                            messages={messages}
                            onCancel={() => setEditingAnswerId(null)}
                            onConfirm={(text) =>
                              handleUpdateAnswer(answer.id, text)
                            }
                          />
                        ) : (
                          <TextWithLineBreaks text={answer.text} />
                        )}
                      </div>

                      {canEditAnswer ? (
                        <div className="actions">
                          <MoreOptions renderAsFixed={true}>
                            <Button
                              category={
                                editingAnswerId === answer.id
                                  ? 'default'
                                  : 'light'
                              }
                              icon="edit"
                              onClick={() => setEditingAnswerId(answer.id)}
                              text={messages.commonEditAnswer}
                            />

                            <ConfirmButton
                              actionCategory="danger"
                              actionText={messages.commonDelete}
                              buttonIcon="trash-alt"
                              buttonText={messages.commonDeleteAnswer}
                              category="light"
                              confirmText={messages.commonDeleteAnswerConfirm}
                              confirmTitle={messages.commonDeleteAnswer}
                              onConfirm={() => handleDeleteAnswer(answer.id)}
                            />
                          </MoreOptions>
                        </div>
                      ) : null}
                    </div>

                    <div className="added-by">
                      <AchievementItemAddedBy
                        date={answer.date}
                        enableUserLink={true}
                        user={answer.user}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {canCreateAnswer && !editingAnswerId ? (
            <div className="form">
              <AnswerForm messages={messages} onConfirm={handleAddAnswer} />
            </div>
          ) : null}

          {canMarkComplete ? (
            <ConfirmButton
              actionCategory="default"
              actionText={messages.commonConfirm}
              buttonSize="small"
              buttonStyle={{ marginTop: 20 }}
              category="lighter"
              onConfirm={handleToggleItemCompletion}
              {...(!item.answers?.length
                ? {
                    disabled: true,
                    buttonTooltip:
                      messages.achievementGroupCompletionMarkDisabled
                  }
                : {})}
              {...(item.isCompleted
                ? {
                    buttonIcon: 'ban',
                    buttonText: messages.commonMarkAsIncomplete,
                    confirmText:
                      messages.achievementGroupCompletionMarkIncompleteConfirm,
                    confirmTitle: messages.commonMarkAsIncomplete,
                    successMessage:
                      messages.achievementGroupCompletionMarkIncompleteConfirmSuccess
                  }
                : {
                    buttonIcon: 'check',
                    buttonText: messages.commonMarkAsComplete,
                    confirmText:
                      messages.achievementGroupCompletionMarkCompleteConfirm,
                    confirmTitle: messages.commonMarkAsComplete,
                    successMessage:
                      messages.achievementGroupCompletionMarkCompleteConfirmSuccess
                  })}
            />
          ) : null}
        </div>

        <style jsx>{`
          .content {
            align-items: flex-start;
            display: flex;
            padding: 5px 0;
          }
          .status {
            display: flex;
            flex-direction: column;
            flex-grow: 0;
            flex-shrink: 0;
            gap: 10px;
            margin-right: 15px;
          }
          .item {
            flex-grow: 1;
            flex-shrink: 1;
            margin-right: 30px;
            padding: 1px 0;
          }
          .item-inner {
            max-width: 720px;
          }
          .item-text {
            color: ${theme.textColorLight};
            font-size: 1.5rem;
            font-weight: 600;
            line-height: 1.4;
          }
          .answers {
            display: flex;
            flex-direction: column;
            gap: 15px;
          }
          .answer {
            align-items: flex-start;
            display: flex;
            flex-direction: row;
            gap: 10px;
            margin-top: 10px;
          }
          .comment {
            background-color: ${theme.lightGreyLighter};
            border-radius: 7px;
            color: ${theme.textColorLight};
            font-size: 1.4rem;
            font-weight: 400;
            flex-grow: 1;
            padding: 10px;
          }
          .actions {
            display: flex;
            flex-direction: row;
            gap: 10px;
          }
          .added-by {
            margin-top: 10px;
          }
          .form {
            background-color: ${theme.lightGreyLighter};
            border-radius: 7px;
            flex-grow: 1;
            margin: 25px 0 5px;
            padding: 15px;
          }
        `}</style>
      </div>
    </BoxRow>
  );
};

export default AchievementGroupsOfUsersGroupFillTemplate;
